import { isValidElement, type JSX } from "react";

import { uniq } from "lodash";

import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import { allTrackers } from "app/services/track/track";

import { MAX_SELECTED_DOMAINS } from "./Consts";
import { EllipsisDropdownItemWrapper, TextWrapper } from "./StyledComponents";
import type { IChipItemWithOptionType, IDomains, IDomainsList } from "./Types";
import { DropdownOptionListType } from "./Types";

const getActivationSetDomainsById = (id: string, activationSets: ActivationObjectSchema[]): string[] => {
    const matchedSet = getActivationSetById(id, activationSets);
    return matchedSet?.allDomains ?? [];
};

const getActivationSetById = (
    id: string,
    activationSets: ActivationObjectSchema[],
): ActivationObjectSchema | undefined => {
    return activationSets?.find((set) => set.id === id);
};
export const transformDomainsData = (domainsList: IDomains[]): IDomainsList[] => {
    return domainsList?.map((domain: IDomains) => {
        return {
            id: domain.Name,
            text: domain.Name,
            imageUrl: domain.Favicon,
            type: DropdownOptionListType.DOMAIN,
        };
    });
};
const transformedProjectsData = (activationObjects: ActivationObjectSchema[]): IDomainsList[] => {
    return activationObjects.map((activationObject) => ({
        id: activationObject.id,
        text: activationObject.name ?? "",
        sharedBy: activationObject.sharedBy,
        type: DropdownOptionListType.PROJECT,
    }));
};

const renderItem = (item: IChipItemWithOptionType, activationSets: ActivationObjectSchema[]) => {
    const domainsInProject = getActivationSetDomainsById(item.id, activationSets)?.length;

    if (item.type === DropdownOptionListType.PROJECT) {
        return (
            <>
                <TextWrapper id={item.text}>{item.text} </TextWrapper>({domainsInProject})
            </>
        );
    } else {
        return <TextWrapper id={item.text}>{item.text} </TextWrapper>;
    }
};
export const renderDropDownItem = (
    selectedItems: IChipItemWithOptionType[],
    domainsOptions: IChipItemWithOptionType[],
    activationSets: ActivationObjectSchema[],
) =>
    function (item: IChipItemWithOptionType): JSX.Element {
        const isItemSelected = selectedItems.some((selectedItem) => selectedItem.id === item.id);

        if (isValidElement(item)) {
            return item;
        }
        return (
            <EllipsisDropdownItemWrapper
                showCheckBox
                key={item.id}
                id={item.id}
                imageUrl={item.imageUrl}
                hasBorderedIcons
                {...(!item.imageUrl && {
                    iconName: "open-folder",
                    iconColor: colorsPalettes.carbon[400],
                    iconSize: "sm",
                })}
                disabled={
                    !isItemSelected ? countDomains(selectedItems, activationSets) >= MAX_SELECTED_DOMAINS : undefined
                }
            >
                {renderItem(item, activationSets)}
            </EllipsisDropdownItemWrapper>
        );
    };

const countDomains = (selectedItems: IChipItemWithOptionType[], activationSets: ActivationObjectSchema[]): number => {
    const uniqueDomains = new Set<string>();
    for (const item of selectedItems) {
        if (item.type === DropdownOptionListType.DOMAIN) {
            uniqueDomains.add(item.id);
        } else if (item.type === DropdownOptionListType.PROJECT) {
            const projectDomains = getActivationSetDomainsById(item.id, activationSets);
            projectDomains.forEach((domain) => {
                uniqueDomains.add(domain);
            });
        }
    }
    return uniqueDomains.size;
};

export const renderChipTitleText = (
    selectedItems: IChipItemWithOptionType[],
    activationSets: ActivationObjectSchema[],
) => {
    const selectedDomainsCount = countDomains(selectedItems, activationSets);
    if (selectedDomainsCount === 0) {
        return "";
    }
    return `${translate("keywords.analysis.filters.domains.projects.button")} (${selectedDomainsCount})`;
};

export const createDomainsStringIds = (
    selectedItems: IChipItemWithOptionType[],
    domainsList: IDomainsList[],
    activationSets: ActivationObjectSchema[],
) => {
    const isSelectedAll = domainsList.length === selectedItems.length;

    if (isSelectedAll) {
        return "";
    }

    const newDomainsArray = selectedItems.reduce<string[]>((acc, item) => {
        if (item.type === DropdownOptionListType.DOMAIN) {
            acc.push(item.id);
        } else {
            const project = getActivationSetById(item.id, activationSets);
            if (project) {
                acc.push(`*${project.id}`);
            }
        }
        return acc;
    }, []);

    return newDomainsArray.join(",");
};

export const createUniqueListOfAllDomains = (domainsStringIds: string[], activationSet: ActivationObjectSchema[]) => {
    if (!domainsStringIds || domainsStringIds.length === 0) {
        return;
    }

    let allDomains: string[] = [];
    const projectIds = domainsStringIds.filter((domain) => domain.startsWith("*"));
    const nonProjectDomains = domainsStringIds.filter((domain) => !domain.startsWith("*"));
    if (projectIds.length > 0) {
        const projectDomains = projectIds.flatMap((projectId) => {
            const cleanProjectId = projectId.substring(1);

            return (
                uniq(activationSet?.find((activationObject) => activationObject.id === cleanProjectId)?.allDomains) ??
                []
            );
        });

        allDomains = [...projectDomains, ...nonProjectDomains];
    } else {
        allDomains = domainsStringIds;
    }

    const uniqueDomains = Array.from(new Set(allDomains)).join(",");
    return uniqueDomains;
};

export const onItemClick = (
    item: IChipItemWithOptionType,
    selectedItems: IChipItemWithOptionType[],
    domainsList: IDomainsList[],
): IChipItemWithOptionType[] => {
    const trackEvent = (isRemoved: boolean) => {
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `${isRemoved ? "Remove" : "Add"} Domain/Project ${item.text}`,
        );
    };

    const selectedIndex = selectedItems.findIndex((i) => i.id === item.id && i.type === item.type);
    trackEvent(selectedIndex !== -1);

    return selectedIndex === -1
        ? [...selectedItems, item]
        : [...selectedItems.slice(0, selectedIndex), ...selectedItems.slice(selectedIndex + 1)];
};

export const getFilteredDomain = (
    domains: IDomainsList[],
    activationSets: ActivationObjectSchema[],
): IDomainsList[] => {
    const transformedProject = activationSets ? transformedProjectsData(activationSets) : [];

    const combinedData: IDomainsList[] = [
        ...transformedProject,
        ...domains?.filter((item) => !transformedProject?.some((project) => project.id === item.id)),
    ];
    return combinedData;
};
